<template>
  <div class="preorder">
    <div class="card">
      <div class="card-header">
        <h3>Listado de preordenes</h3>
      </div>
      <div>
      <div tabindex="0" ref="successServices" class="alerts">
        <CAlert
          color="success"
          :closeButton="true"
          :show.sync="success.hasSuccess"
        >
          {{ success.message }}
        </CAlert>
      </div>
      </div>
      <div class="card-body">
        <CDataTable
          :items="items"
          :fields="fields"
          :noItemsView="noItemsView"
          hover
          :sorter="{ external: true }"
          :sorterValue.sync="sort"
          @row-clicked="onRowClicked"
          class="main-table"
        >
          <!-- filters -->
          <template #over-table>
            <div class="filters-section">
              <div>
                <div class="open-filter">
                  <input
                    v-model.trim="openFilterInput"
                    type="text"
                    placeholder="Teléfono"
                    maxlength="10"
                    v-c-tooltip="'Búsqueda de preordenes por número telefónico del cliente'"
                    @keypress="alphaNumeric($event)"
                  />
                  <button
                    class="btn btn-primary"
                    v-c-tooltip="
                      'Ejecuta la acción de búsqueda de lo ingresado en el campo'
                    "
                    @click="search()"
                  >
                    Buscar
                  </button>
                  <button
                    class="btn btn-light"
                    @click="resetFilters()"
                    v-c-tooltip="
                      'Limpia los filtros y retorna todas las preordenes'
                    "
                  >
                    Limpiar
                  </button>
                </div>
                <div>
                  <date-range-picker
                    v-model="dateRange"
                    :locale-data="{
                      format: 'dd/mm/yyyy',
                      daysOfWeek: daysOfWeekSpanish,
                      applyLabel: 'Aplicar',
                      cancelLabel: 'Cancelar',
                    }"
                    :ranges="false"
                    opens="left"
                    :append-to-body="false"
                    :date-format="disabledDates"
                    @update="dateRangeFilter"
                    class="date-range"
                    v-c-tooltip="
                      'Filtra las preordenes por el rango de fecha indicado'
                    "
                  >
                    <template v-slot:input="picker" style="min-width: 350px">
                      <span v-if="!picker.startDate && !picker.endDate">
                        Ej: 01/01/2020 - 03/09/2020
                      </span>
                      <span v-else>
                        {{ formatToDatePicker(picker.startDate) }} -
                        {{ formatToDatePicker(picker.endDate) }}
                      </span>
                    </template>
                  </date-range-picker>
                </div>
              </div>
            </div>
          </template>
          <!-- custom table fields -->
          <template #customerPhoneNumber="{ item }">
            <td>{{ item.customerPhoneNumber }}</td>
          </template>
          <template #origin="{ item }">
            <td>{{ getPreorderOriginText(item.origin) }}</td>
          </template>
          <template #serviceDate="{ item }">
            <td>{{ formatDate(item.serviceDate) }}</td>
          </template>
          <template #statusId="{ item }">
            <td>{{ getStatusText(item.statusId) }}</td>
          </template>
          <template #“serviceType”="{ item }">
            <td>{{ item.serviceType }}</td>
          </template>
          <template #folio="{ item }">
            <td>{{ item.folio }}</td>
          </template>
          <template #actions="{ item }">
            <td class="py-2">
              <div>
                <CButton
                  v-if="canBeRejected(+item.statusId)"
                  :id="`reject_${item.registryUuid}`"
                  color="danger"
                  style="margin-right:5px"
                  variant="outline"
                  square
                  size="sm"
                  @click="openPrompt(false)"
                >
                  Rechazar
                </CButton>
                <b-tooltip v-if="canBeRejected(+item.statusId)" :target="`reject_${item.registryUuid}`" placement="left">
                  Rechazar preorden
                </b-tooltip>
                <CButton
                  v-if="canBeAccepted(+item.statusId)"
                  :id="`accept_${item.registryUuid}`"
                  color="success"
                  variant="outline"
                  square
                  size="sm"
                  @click="openPrompt(true)"
                >
                  Aceptar
                </CButton>
                <b-tooltip v-if="canBeAccepted(+item.statusId)" :target="`accept_${item.registryUuid}`" placement="left">
                  Aceptar preorden
                </b-tooltip>
              </div>
            </td>
          </template>
        </CDataTable>
        <CPagination :active-page.sync="page" :pages="pages" align="center" />
      </div>
      <div class="card-footer">
        <p>
          Total de registros: <strong>{{ totalRecords }}</strong>
        </p>
      </div>
    </div>
    <PreorderDetailModal
      v-if="createModal.isActive"
      :isModalActive="createModal.isActive"
      :infoModal="createModal.data"
      @onClose="closeCreateModal"
      @onSuccess="onSuccessCreation"
    />
    <CModal
      :title="`${promptTitle} preorden`"
      :show.sync="promptModal"
      :closeOnBackdrop="false"
      class="prompt-modal"
    >
      <div>
        ¿Desea <b>{{promptTitle}}</b> la preorden de <i>{{selectedItem.serviceType}}</i> del usuario {{selectedItem.customerPhoneNumber}}?
      </div>
      <template #footer>
        <button class="btn btn-dark" @click="promptModal = false">
          Salir
        </button>
        <button :class="promptClass" :disabled="workingPrompt" @click="promptAction()">
          {{promptTitle}}
        </button>
      </template>
    </CModal>
  </div>
</template>

<script>
/* eslint-disable import/no-extraneous-dependencies */
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker'
import queryParser from '../utils/queryparser'
import { getErrorMessage } from '../common/errors'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import PreorderDetailModal from '../components/preorders/PreorderDetail'
import {
  STATUS_PREORDERS,
  SERVICE_ORIGIN,
  VALIDATIONS
} from '../common/enums'

const fields = [
  { key: 'customerPhoneNumber', label: 'Teléfono', sorter: false },
  { key: 'origin', label: 'Origen', sorter: true },
  { key: 'serviceDate', label: 'Fecha del Servicio', sorter: true },
  { key: 'statusId', label: 'Estatus', sorter: true },
  { key: 'serviceType', label: 'Tipo de Servicio', sorter: true },
  { key: 'folio', label: 'Folio Servicio', sorter: true },
  {
    key: 'actions',
    label: 'Acción',
    _style: 'width:15%',
    sorter: false,
    filter: false
  }
]
export default {
  name: 'Preorders',
  components: {
    DateRangePicker,
    PreorderDetailModal
  },
  data() {
    return {
      selectedItem: {},
      noItemsView: {
        noResults: 'Registros no encontrados.',
        noItems: 'Registros no encontrados.'
      },
      createModal: {
        isActive: false,
        data: {}
      },
      promptTitle: '',
      promptClass: 'btn btn-',
      promptModal: false,
      workingPrompt: false,
      promptValue: false,
      items: [],
      daysOfWeekSpanish: ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
      fields,
      page: 1,
      pages: 0,
      totalRecords: 0,
      dateRange: {
        startDate: '',
        endDate: ''
      },
      availableDateRange: {
        startDate: '',
        endDate: ''
      },
      startDate: '',
      endDate: '',
      sort: {
        column: 'createdAt',
        asc: false
      },
      activeModal: false,
      alertError: false,
      error: {},
      openFilterInput: '',
      success: {
        hasSuccess: false,
        message: ''
      },
      refreshInterval: null,
    }
  },
  computed: {
    ...mapGetters('auth')
  },
  watch: {
    page() {
      this.getPreorders()
    },
    sort() {
      this.getPreorders()
    },
    openFilterInput(value) {
      this.openFilterInput = this.openFilterInput.toUpperCase()
      if (!value.length || value.length >= VALIDATIONS.MIN_TEXT_LENGTH) {
        this.getPreorders()
      }
    },
    items() {
      this.getPreordersLength();
    }
  },
  async created() {
    this.getPreordersLength();
    this.refreshInterval = setInterval(() => {
      this.getPreorders()
    }, 20000);
  },
  async beforeUpdate() {
    this.getPreordersLength();
  },
  async mounted() {
    this.getPreorders()
  },
  beforeDestroy() {
    clearInterval(this.refreshInterval);
  },
  unmounted() {
    clearInterval(this.refreshInterval);
  },
  methods: {
    ...mapActions([
      'getPreordersLength'
    ]),
    async onSuccessCreation() {
      this.closeCreateModal()
      this.setSuccessMessage('Preorden modificada con éxito.')
      this.scrollTopSuccess()
      await this.filter()
      this.getPreorders()
    },
    async promptAction() {
      try {
        this.workingPrompt = true
        const { data } = await this.$axios.put('preorder/status', {
          registryUuid: this.selectedItem.registryUuid,
          statusId: this.promptValue ? STATUS_PREORDERS.FINISHED.id : STATUS_PREORDERS.REJECTED.id
        })
        let successMessage = 'Se aceptó la preorden y se generó un servicio'
        
        if(data && data.folio) {
          successMessage += ` con folio ${data.folio}`
        }
        this.success.message = successMessage
        this.success.hasSuccess = true
        this.selectedItem = {}
        this.promptModal = false
        this.workingPrompt = false
        this.getPreorders()
      } catch (er) {
            this.workingPrompt = false
      }
    },
    setSuccessMessage(message) {
      this.success.hasSuccess = true
      this.success.message = message
    },
    openCreateModal() {
      this.createModal.isActive = true
    },
    getPreorderOriginText(origin) {
      const enumValue = SERVICE_ORIGIN[Object.keys(SERVICE_ORIGIN).find(name => SERVICE_ORIGIN[name].name === origin)] 

      return enumValue ? enumValue.display : SERVICE_ORIGIN.ADMIN.display
    },
    getStatusText(statusId) {
      const enumValue = STATUS_PREORDERS[Object.keys(STATUS_PREORDERS).find(name => +STATUS_PREORDERS[name].id === +statusId)]
      return enumValue ? enumValue.name : '(Desconocido)'
    },
    openPrompt(status) {
      this.promptTitle = status ? 'Aceptar' : 'Rechazar'
      this.promptClass = status ? 'btn btn-success' : 'btn btn-danger'
      this.promptValue = status
      this.promptModal = true
    },
    canBeRejected(statusId) {
      return [
        STATUS_PREORDERS.CREATED.id,
        STATUS_PREORDERS.VALIDATED.id,
        STATUS_PREORDERS.DURING_VALIDATION.id
        ].includes(statusId)
    },
    canBeAccepted(statusId) {
      return [
        STATUS_PREORDERS.VALIDATED.id
        ].includes(statusId)
    },
    getPreorders() {
      const filters = this.getFilters()
      const queryString = queryParser.parse(filters)

      this.$axios
        .get(`preorder?${queryString}`)
        .then(response => {
          const {data} = response
          this.items = data.data
          this.pages = data.pagination.totalPages
          this.totalRecords = data.pagination.total

          this.items.map((item) => {
            item.createdAt = moment.tz(item.createdAt, 'America/Mexico_City').format('DD/MM/YYYY hh:mm A')
            item.cancelledAt = moment.tz(item.cancelledAt, 'America/Mexico_City').format('DD/MM/YYYY hh:mm A')

            return item
          })

          if (data.pagination.dates) {
              this.availableDateRange.startDate = new Date(
                `${data.pagination.dates.min}T00:00:00`
              )
              this.availableDateRange.endDate = new Date(
                `${data.pagination.dates.max}T00:00:00`
              )
            }
          return this.items
        })
        .catch((err) => {
          this.error = getErrorMessage(err)
        })
    },
    scrollTopSuccess() {
      this.$nextTick(() => {
        this.$refs.successServices.focus()
      })
    },
    getFilters() {
      const options = {
        state: this.state,
        page: this.page
      }
      if (this.startDate && this.endDate) {
        options.nextServiceDate = `${this.startDate}:${this.endDate}`
      }

      if (this.openFilterInput && this.openFilterInput.trim() !== '') {
        const value = this.openFilterInput.trim().toUpperCase()
        this.openFilterInput = value
        options.customerPhoneNumber = value
      }

      return options
    },
    formatDate(date) {
      return moment.utc(date, 'YYYYMMDD HH:mm:ss').format('DD/MM/YYYY hh:mm A')
    },
    filter() {
      const options = {
        page: this.page
      }
      if (this.startDate && this.endDate) {
        options.serviceDate = `${this.startDate}:${this.endDate}`
      }
      if (this.sort.asc !== null) {
        options.orderBy = this.sort.column
        options.sortBy = this.sort.asc ? 'ASC' : 'DESC'
      }
      if (this.openFilterInput && this.openFilterInput.trim() !== '') {
        options.customerPhoneNumber = this.openFilterInput
      }

      return options
    },
    dateRangeFilter() {
      this.startDate = moment(this.dateRange.startDate).format('YYYY-MM-DD')
      this.endDate = moment(this.dateRange.endDate).format('YYYY-MM-DD')
      this.getPreorders()
    },
    alphaNumeric(e) {
      const key = String.fromCharCode(e.charCode)
      if (!/^[A-Za-zÑñÁÉÍÓÚÜáéíóúü0-9 ]$/.test(key)) {
        e.preventDefault()
      }
    },
    async openModal(item) {
      await this.$axios
        .get(`/preorder/${item.registryUuid}`)
        .then((response) => {
          this.alertError = false
          const { data } = response

          if (!data.ServiceTypeComplete) data.ServiceTypeComplete = { registryUuid: data.serviceType.registryUuid }
          if (!data.serviceTypeCategory) data.serviceTypeCategory = { registryUuid: 
            data.details.length && data.details[0].category ? data.details[0].category.registryUuid : null }
          if (!data.amount) data.amount = data.details[0].units
          if (!data.companyUuid) data.companyUuid =  data.company ? data.company.registryUuid : null
          if (!data.name) data.name = data.customer.name
          if (!data.phoneNumber) data.phoneNumber = data.customer.phoneNumber
          if (!data.origin) data.origin = SERVICE_ORIGIN.ADMIN.name
          this.createModal.data = data
          this.createModal.isActive = true
        })
        .catch((err) => {
          console.log(err)
          this.error = getErrorMessage({
            response: { data: { codeApi: 'EA0015' } }
          })
          this.alertError = true
          this.createModal.data = {}
        })
    },
    closeCreateModal() {
      this.createModal.isActive = false
    },
    onRowClicked(item, index, event, e) {
      if(e.srcElement.type === 'button') {
        return this.selectedItem = item
      }
      this.openModal(item)
    },
    resetFilters() {
      this.startDate = null
      this.endDate = null
      this.dateRange.startDate = null
      this.dateRange.endDate = null
      this.page = 1
      this.sort.column = 'serviceDate'
      this.sort.asc = false
      this.openFilterInput = ''
      this.getPreorders()
    },
    disabledDates(classes, date) {
      const getCalendarDate = moment(date.getTime()).format()

      if (!classes.disabled) {
        classes.disabled = getCalendarDate > moment(this.availableDateRange.endDate).format()
      }
      return classes
    },
    formatToDatePicker(date) {
      return moment(date).format('DD/MM/YYYY')
    },
    search() {
      if (this.openFilterInput.length || this.openFilterInput.length >= VALIDATIONS.MIN_TEXT_LENGTH) {
        this.getPreorders()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.preorder {
  .mr-5{
    margin-right: 5px;
  }
  .card {
    .card-header,
    .card-footer {
      display: flex;
      p {
        margin: auto 0 auto auto;
      }
    }
    .card-body {
      .date-range {
        margin-bottom: 8px;
      }
      .filters-section {
        display: flex;
        flex-direction: column;
        margin-bottom: 16px;
        > div {
          display: flex;
          flex-direction: column;
          &:first-child {
            > div {
              &:last-child {
                .date-range {
                  width: 100%;
                  @media (min-width: 992px) {
                    width: auto;
                  }
                }
                @media (min-width: 992px) {
                  margin-left: auto;
                }
              }
            }
          }
          @media (min-width: 992px) {
            flex-direction: row;
          }
        }
        .open-filter {
          margin-bottom: 16px;
          display: flex;
          input {
            height: 35px;
            box-sizing: content-box;
            padding: 0 16px;
            border: 1px solid #ccc;
            border-radius: 4px;
            width: calc(100% - 165px);
          }
          button {
            height: 35px;
            border-radius: 4px;
            margin-left: 4px;
          }
        }
        .select-filters-wrapper {
          display: flex;
          flex-direction: column;
          @media (min-width: 992px) {
            flex-direction: row;
          }
          .select-filter {
            display: flex;
            flex-direction: column;
            margin-right: 8px;
            &:last-child {
              margin-right: 0;
            }
          }
        }
      }
    }
  }
  .text-wrapper {
    > div {
      margin-bottom: 8px;
    }
  }
  .tabs-selector {
    border: 1px solid #ccc;
    display: flex;
    height: 35px;
    border-radius: 4px;
    button {
      border: none;
      background-color: #fff;
      outline: none;
      color: #000015;
      width: calc(100% / 3);
      @media (min-width: 992px) {
        width: 53px;
      }
      border-right: 1px solid #ccc;
      border-radius: 4px;
      &.active {
        background-color: #5b2ae0;
        color: white;
      }
      &:last-child {
        border-right: 0;
      }
    }
  }
}
</style>
